<template>
  <aside class="lyra-billet" :data-blocked="suggestion.canApply === false">
    <div class="lyra-billet__seal" aria-hidden="true">✦</div>
    <header>
      <p>Billet de Lyra</p>
      <h4>{{ suggestion.canApply === false ? 'Cette demande mérite une autre orientation' : 'Voilà ce que j’ai compris' }}</h4>
    </header>

    <template v-if="suggestion.canApply !== false">
      <div class="lyra-billet__draft">
        <strong>{{ suggestion.title }}</strong>
        <p>{{ suggestion.description }}</p>
      </div>
      <div v-if="suggestion.questions?.length" class="lyra-billet__questions">
        <b>{{ formal ? 'À préciser si vous le souhaitez' : 'À préciser si tu le souhaites' }}</b>
        <ul><li v-for="question in suggestion.questions" :key="question">{{ question }}</li></ul>
      </div>
    </template>

    <p v-if="suggestion.note" class="lyra-billet__note">{{ suggestion.note }}</p>
    <p class="lyra-billet__control">{{ formal ? 'Rien n’est modifié sans votre accord.' : 'Rien n’est modifié sans ton accord.' }}</p>

    <div class="lyra-billet__actions">
      <button v-if="suggestion.canApply !== false" type="button" class="lyra-billet__apply" @click="$emit('apply')">
        Utiliser cette version
      </button>
      <button type="button" class="lyra-billet__dismiss" @click="$emit('dismiss')">
        {{ suggestion.canApply === false
          ? (formal ? 'Reprendre votre texte' : 'Reprendre mon texte')
          : (formal ? 'Garder votre texte' : 'Garder mon texte') }}
      </button>
    </div>
  </aside>
</template>

<script setup lang="ts">
import type { QuestRewrite } from './questRewrite';

withDefaults(defineProps<{ suggestion: QuestRewrite; formal?: boolean }>(), { formal: false });
defineEmits<{ (event: 'apply'): void; (event: 'dismiss'): void }>();
</script>

<style scoped>
.lyra-billet {
  --billet-accent: var(--brass, var(--gold, #b9852d));
  position: relative;
  display: grid;
  gap: 12px;
  padding: 18px 18px 16px 22px;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--billet-accent) 48%, transparent);
  border-radius: 5px 14px 7px 10px;
  color: inherit;
  background:
    linear-gradient(95deg, color-mix(in srgb, var(--billet-accent) 8%, transparent), transparent 34%),
    color-mix(in srgb, currentColor 4%, transparent);
  box-shadow: 3px 5px 0 color-mix(in srgb, var(--billet-accent) 10%, transparent);
}
.lyra-billet::before {
  content: '';
  position: absolute;
  inset: 7px;
  border: 1px dashed color-mix(in srgb, var(--billet-accent) 24%, transparent);
  pointer-events: none;
}
.lyra-billet > * { position: relative; }
.lyra-billet__seal {
  position: absolute;
  top: 12px;
  right: 14px;
  display: grid;
  place-items: center;
  width: 31px;
  height: 31px;
  border: 1px solid var(--billet-accent);
  border-radius: 50%;
  color: var(--billet-accent);
  box-shadow: 0 0 10px color-mix(in srgb, var(--billet-accent) 22%, transparent);
}
.lyra-billet header { padding-right: 42px; }
.lyra-billet header p { margin: 0; color: var(--billet-accent); font-size: .67rem; font-weight: 900; letter-spacing: .12em; text-transform: uppercase; }
.lyra-billet h4 { margin: 3px 0 0; font-family: var(--font-title, 'Crimson Pro', Georgia, serif); font-size: 1.05rem; }
.lyra-billet__draft { padding: 11px 13px; border-left: 3px solid var(--billet-accent); background: color-mix(in srgb, var(--billet-accent) 7%, transparent); }
.lyra-billet__draft strong { display: block; margin-bottom: 4px; font-family: var(--font-title, 'Crimson Pro', Georgia, serif); font-size: 1rem; }
.lyra-billet__draft p, .lyra-billet__note, .lyra-billet__control { margin: 0; line-height: 1.5; }
.lyra-billet__draft p { white-space: pre-wrap; font-size: .82rem; }
.lyra-billet__questions { font-size: .76rem; }
.lyra-billet__questions b { color: var(--billet-accent); }
.lyra-billet__questions ul { margin: 5px 0 0; padding-left: 18px; }
.lyra-billet__note { font-size: .78rem; font-weight: 700; }
.lyra-billet__control { opacity: .68; font-size: .69rem; font-style: italic; }
.lyra-billet__actions { display: flex; flex-wrap: wrap; gap: 9px; }
.lyra-billet__actions button { min-height: 42px; padding: 8px 13px; border-radius: 5px; font: inherit; font-size: .76rem; font-weight: 800; cursor: pointer; }
.lyra-billet__apply { border: 1px solid var(--billet-accent); color: inherit; background: color-mix(in srgb, var(--billet-accent) 18%, transparent); }
.lyra-billet__dismiss { border: 1px solid color-mix(in srgb, currentColor 22%, transparent); color: inherit; background: transparent; }
.lyra-billet[data-blocked='true'] { border-color: color-mix(in srgb, #b75b50 58%, transparent); }
@media (max-width: 520px) {
  .lyra-billet { padding: 16px 14px; }
  .lyra-billet__actions { display: grid; }
  .lyra-billet__actions button { width: 100%; }
}
</style>
